import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BillingProfilePaymentMethod {
  id: string;
  stripe_payment_method_id: string;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
  is_default: boolean;
}

export interface BillingProfile {
  id: string;
  owner_id: string;
  stripe_customer_id: string | null;
  default_payment_method_id: string | null;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
  created_at: string;
  updated_at: string;
  payment_methods?: BillingProfilePaymentMethod[];
}

export interface PaymentMethodInfo {
  payment_method_id: string;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
}

// Billing profile query key (uses user.profile as base since it's user-specific)
const billingProfileKey = ['billing-profile'] as const;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/** Invoke Edge Function with explicit session; throws with server error or "Please sign in again" if no session. */
async function invokeBillingFunction<T>(
  name: string,
  body?: Record<string, unknown>
): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error("Please sign in again");

  const res = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: supabaseAnonKey,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) throw new Error(data?.error || res.statusText || `Request failed (${res.status})`);
  return data as T;
}

// Fetch or create billing profile via edge function (includes payment_methods list)
export const useBillingProfile = () => {
  return useQuery({
    queryKey: billingProfileKey,
    queryFn: () =>
      invokeBillingFunction<{
        stripe_customer_id: string;
        default_payment_method_id: string | null;
        card_brand: string | null;
        card_last4: string | null;
        card_exp_month: number | null;
        card_exp_year: number | null;
        payment_methods?: BillingProfilePaymentMethod[];
      }>("create-stripe-customer"),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1,
  });
};

// Set default payment method (stripe_payment_method_id)
export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { payment_method_id: string }) =>
      invokeBillingFunction<{ success: boolean }>("set-default-payment-method", {
        payment_method_id: args.payment_method_id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingProfileKey });
    },
  });
};

// Create SetupIntent for adding a payment method. Backend uses same key preference as create-stripe-customer and returns stripe_mode so frontend can match.
export const useCreateSetupIntent = () => {
  return useMutation({
    mutationFn: (args: { stripe_mode: "test" | "live" }) =>
      invokeBillingFunction<{ client_secret: string; setup_intent_id: string; stripe_mode: "test" | "live" }>(
        "create-stripe-setup-intent",
        { stripe_mode: args.stripe_mode }
      ),
  });
};

// Save payment method. payment_method_id from Stripe confirmCardSetup and stripe_mode (same as create) are required.
export const useSavePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { payment_method_id: string; stripe_mode: "test" | "live" }) =>
      invokeBillingFunction<PaymentMethodInfo>("save-payment-method", {
        payment_method_id: args.payment_method_id,
        stripe_mode: args.stripe_mode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingProfileKey });
    },
  });
};
